import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPlayer, destroyPlayer } from '@atc/videojs-player';

import VideoStatus from './VideoStatus';
import { getCarflixMedia } from '../../helpers/videos';

const CarflixVideoPlayer = ({ video }) => {
	const videoRef = useRef(null);
	const { id, uri } = video || {};
	const { src, poster } = getCarflixMedia(uri);

	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		const player = createPlayer(videoRef.current, {
			src,
			image: poster,
			videoId: id ? `${id}` : undefined
		});

		player.ready(() => player.volume(1));

		let hasStarted = false;
		player.on('play', () => {
			if (!hasStarted) setIsLoading(true);
		});
		player.on('playing', () => {
			hasStarted = true;
			setIsLoading(false);
		});
		player.on(['waiting', 'seeking'], () => setIsLoading(true));
		player.on(['canplay', 'seeked'], () => {
			if (hasStarted) setIsLoading(false);
		});
		player.on(['pause', 'ended', 'error'], () => setIsLoading(false));

		return () => {
			if (!player.isInPictureInPicture()) {
				destroyPlayer(player);
				return;
			}
			let isDestroyed = false;
			const destroyOnce = () => {
				if (isDestroyed) return;
				isDestroyed = true;
				destroyPlayer(player);
			};
			player.pause();
			player.one('leavepictureinpicture', destroyOnce);
			Promise.resolve(player.exitPictureInPicture())
				.catch(() => {})
				.then(destroyOnce);
		};
	}, [src, poster, id]);

	return (
		<div className="h-100 video-container videojs-container">
			<div className="videojs-frame">
				<VideoStatus isLoading={isLoading} />
				<div>
					{/* eslint-disable-next-line jsx-a11y/media-has-caption */}
					<video
						ref={videoRef}
						className="video-js"
						controls
						preload="metadata"
						data-testid="videojs-player"
					/>
				</div>
			</div>
		</div>
	);
};

CarflixVideoPlayer.propTypes = {
	video: PropTypes.shape({
		id: PropTypes.number,
		uri: PropTypes.string
	})
};

CarflixVideoPlayer.defaultProps = {
	video: {}
};

export default CarflixVideoPlayer;
